import React, { useEffect, useMemo, useCallback, memo, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';

// Styles
import './SideNavBar.css';

// Version
import packageJson from '../../../../package.json';

// Models
import SubscriptionPlan from 'src/models/SubscriptionPlan';

// Store
import { useHasAnyAccount, useAccountStore } from 'src/stores/account.store';
import { useUsername, useUserFullName, useUserStore } from 'src/stores/user.store';

// Environment
import environment from 'src/settings/environmentLoader';

// Feature flags
import { featureFlags } from 'src/settings/features';
import { useTenantFeatures } from 'src/settings/features/tenantFeatures';

// License service
import { LicenseService } from 'src/services/enterprise/license.service';

interface SideNavBarProps {
    signOut: () => void;
    subscriptionPlan: SubscriptionPlan;
    toggleNotifier?: (message: string, type: string, options?: { duration?: number; persistent?: boolean }) => void;
}

const SIDEBAR_COLLAPSED_KEY = 'vizcon_sidebar_collapsed';

// SideNavBar Component
const SideNavBar: React.FC<SideNavBarProps> = memo(({ signOut, subscriptionPlan, toggleNotifier }) => {
    const navigate = useNavigate();

    // Collapse state
    const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
        const saved = localStorage.getItem(SIDEBAR_COLLAPSED_KEY);
        return saved === 'true';
    });
    // Tenant features (role/type-based visibility)
    const { showGroupManagement, showUserManagement } = useTenantFeatures();

    // Use Zustand stores (accounts already loaded by App.tsx during init)
    const hasAnyAccount = useHasAnyAccount();
    const error = useAccountStore(state => state.error);
    const clearError = useAccountStore(state => state.clearError);

    // User store
    const username = useUsername();
    const fullName = useUserFullName();
    const getCurrentUser = useUserStore(state => state.getCurrentUser);
    const isAuthenticated = useUserStore(state => state.isAuthenticated);

    // Debug user store values
    // console.log('SideNavBar: User store values:', { username, fullName, isAuthenticated });

    // Load user data if not available
    useEffect(() => {
        if (!username && !fullName) {
            // console.log('SideNavBar: No user data, calling getCurrentUser');
            getCurrentUser();
        }
    }, [username, fullName, getCurrentUser]);

    // Handle account errors
    useEffect(() => {
        if (error && toggleNotifier) {
            toggleNotifier(
                `Failed to load accounts: ${error}`,
                'danger',
                { duration: 8000, persistent: false }
            );
            // Clear the error after showing notification
            clearError();
        }
    }, [error, toggleNotifier, clearError]);

    // Memoized sign out handler
    const handleSignOut = useCallback(() => {
        navigate('/');
        signOut();
    }, [navigate, signOut]);

    // Toggle collapse handler
    const toggleCollapse = useCallback(() => {
        setIsCollapsed(prev => {
            const newValue = !prev;
            localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(newValue));
            return newValue;
        });
    }, []);

    // Navigation items configuration
    const mainNavItems = useMemo(() => [
        {
            to: "/",
            icon: "bi-ethernet",
            label: "Network",
            requiresAccount: true
        },
        {
            to: "/global-network",
            icon: "bi-globe",
            label: "Global network",
            requiresAccount: true
        },
        {
            to: "/serverless",
            image: "/assets/img/menu/lambda.png",
            label: "Serverless",
            requiresAccount: true
        }
    ], []);

    const secondaryNavItems = useMemo(() => [
        {
            to: "/settings",
            icon: "bi-gear",
            label: "Settings"
        },
        {
            to: "/subscriptions",
            icon: "bi-cart",
            label: "Subscription"
        }
    ], []);

    // Render navigation item
    const renderNavItem = useCallback((item: any, index: number) => (
        <li key={index} className="nav-item w-100" role="none">
            <NavLink
                className="nav-link"
                to={item.to}
                role="menuitem"
                aria-label={item.label}
                title={isCollapsed ? item.label : undefined}
            >
                {item.image ? (
                    <img
                        src={item.image}
                        alt={`${item.label} icon`}
                        className="nav-icon"
                        style={{ width: "20.59px" }}
                    />
                ) : (
                    <i className={`fs-4 ${item.icon}`} aria-hidden="true"></i>
                )}
                {!isCollapsed && <span className="ms-2 d-none d-sm-inline">{item.label}</span>}
            </NavLink>
        </li>
    ), [isCollapsed]);

    // Error handling is now done in useEffect above
    // Component continues to render normally even with errors

    return (
        <nav
            className={`d-flex flex-column align-items-center align-items-sm-start px-3 pt-2 min-vh-100 sticky-top ${isCollapsed ? 'sidebar-collapsed' : 'sidebar-expanded'}`}
            role="navigation"
            aria-label="Main navigation"
            style={{ transition: 'all 0.3s ease' }}
        >
            {/* Logo Section with Toggle Button */}
            <div className="pb-3 w-100 d-flex align-items-center justify-content-between">
                {!isCollapsed && <div className="navbar-brand d-flex align-items-center">
                    <img src={environment.branding.logoPath} alt={environment.branding.logoAlt} height="55" className="d-inline-block" />
                    <span className="ms-2 d-none d-sm-inline h2 mb-0">{environment.branding.companyName}</span>
                </div>}
                <button
                    onClick={toggleCollapse}
                    className="btn btn-outline-secondary btn-sm ms-1 d-none d-sm-block"
                    aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                    style={{ fontSize: '1.2rem' }}
                >
                    <i className={`bi ${isCollapsed ? 'bi-chevron-right' : 'bi-chevron-left'}`}></i>
                </button>
            </div>

            {/* Main Navigation */}
            <ul
                className="nav nav-pills flex-column mb-sm-auto mb-0 align-items-center align-items-sm-start"
                role="menubar"
                aria-label="Main navigation menu"
            >
                {/* Network items - only show if user has accounts */}
                {hasAnyAccount && mainNavItems.map(renderNavItem)}

                {/* Accounts - always visible */}
                <li className="nav-item w-100" role="none">
                    <NavLink
                        className="nav-link"
                        to="/accounts"
                        role="menuitem"
                        aria-label="AWS Accounts"
                        title={isCollapsed ? "Accounts" : undefined}
                    >
                        <img
                            src="/assets/img/menu/aws.png"
                            alt="AWS icon"
                            className="nav-icon"
                            style={{ width: "20.59px" }}
                        />
                        {!isCollapsed && <span className="ms-2 d-none d-sm-inline">Accounts</span>}
                    </NavLink>
                </li>

                {/* Admin: Group Management */}
                {showGroupManagement && (
                    <li className="nav-item w-100" role="none">
                        <NavLink
                            className="nav-link"
                            to="/groups"
                            role="menuitem"
                            aria-label="Groups"
                            title={isCollapsed ? "Groups" : undefined}
                        >
                            <i className="fs-4 bi-collection" aria-hidden="true"></i>
                            {!isCollapsed && <span className="ms-2 d-none d-sm-inline">Groups</span>}
                        </NavLink>
                    </li>
                )}

                {/* Admin: User Management */}
                {showUserManagement && (
                    <li className="nav-item w-100" role="none">
                        <NavLink
                            className="nav-link"
                            to="/users"
                            role="menuitem"
                            aria-label="Users"
                            title={isCollapsed ? "Users" : undefined}
                        >
                            <i className="fs-4 bi-people" aria-hidden="true"></i>
                            {!isCollapsed && <span className="ms-2 d-none d-sm-inline">Users</span>}
                        </NavLink>
                    </li>
                )}
            </ul>

            <hr />

            {/* Secondary Navigation */}
            <div className="pb-4">
                {/* License Warning Section */}
                {LicenseService.shouldShowWarning() && (
                    <div className="mb-3 w-100">
                        <div className="alert alert-warning py-2 px-2 small mb-0"
                            style={{ minWidth: '50px' }}
                        >
                            <div className="d-flex align-items-start">

                                <div className="flex-grow-1" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>

                                    <strong className="d-block mb-1">
                                        <i className="bi bi-exclamation-triangle me-2 flex-shrink-0"
                                            style={{ fontSize: '0.9rem', marginTop: '0.1rem' }}></i>License Expiring
                                    </strong>
                                    <div className="text-wrap">
                                        {LicenseService.getWarningMessage()}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <ul
                    className="nav nav-pills flex-column mb-sm-auto mb-0 align-items-center align-items-sm-start"
                    role="menubar"
                    aria-label="Secondary navigation menu"
                >

                    {/* Settings and Subscription */}
                    {secondaryNavItems
                        .filter(item => featureFlags.isSubscriptionsEnabled() || item.to !== '/subscriptions')
                        .map(renderNavItem)}

                    {/* Feature Requests - External Link 
                    <li className="nav-item w-100" role="none">
                        <a
                            className="nav-link"
                            href="https://vizcon.canny.io/feature-requests"
                            target="_blank"
                            rel="noreferrer"
                            role="menuitem"
                            aria-label="Feature requests (opens in new tab)"
                            title={isCollapsed ? "Feature requests" : undefined}
                        >
                            <i className="fs-4 bi-lightbulb" aria-hidden="true"></i>
                            {!isCollapsed && <span className="ms-2 d-none d-sm-inline">Feature requests</span>}
                        </a>
                    </li>
                    <li className="nav-item w-100" role="none">
                        <a
                            className="nav-link"
                            href="https://vizcon.canny.io/changelog"
                            target="_blank"
                            rel="noreferrer"
                            role="menuitem"
                            aria-label="Changelog (opens in new tab)"
                            title={isCollapsed ? "Changelog" : undefined}
                        >
                            <i className="fs-4 bi-arrow-repeat" aria-hidden="true"></i>
                            {!isCollapsed && <span className="ms-2 d-none d-sm-inline">Changelog</span>}
                        </a>
                    </li>
                    */}

                    {/* User Profile Section */}
                    <li className="nav-item border-top" role="none">
                        <div
                            className="nav-link d-flex align-items-center"
                            rel="noreferrer"
                            role="menuitem"
                            aria-label="User"
                            title={isCollapsed ? (fullName || username || 'User') : undefined}
                            style={{ minWidth: 0 }}
                        >
                            <i className="fs-4 bi-person-fill flex-shrink-0" aria-hidden="true"></i>
                            {!isCollapsed && (
                                <span
                                    className="ms-2 d-none d-sm-inline"
                                    style={{
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        maxWidth: '120px'
                                    }}
                                >
                                    {fullName || username || (isAuthenticated ? 'Loading...' : 'User')}
                                </span>
                            )}
                        </div>
                    </li>

                    {/* Sign Out */}
                    <li className="nav-item w-100" role="none">
                        <button
                            className="nav-link"
                            onClick={handleSignOut}
                            role="menuitem"
                            aria-label={`Sign out ${username || 'user'}`}
                            title={isCollapsed ? "Sign out" : undefined}
                        >
                            <i className="fs-4 bi-box-arrow-right" aria-hidden="true"></i>
                            {!isCollapsed && <span className="ms-2 d-none d-sm-inline">Sign out</span>}
                        </button>
                    </li>

                    {/* Version */}
                    {!isCollapsed && (
                        <li className="nav-item text-center" role="none">
                            <small className="nav-version d-block mt-2 ms-3">
                                <span className="d-none d-sm-inline">Version </span>{packageJson.version}
                            </small>
                        </li>
                    )}
                </ul>
            </div>
        </nav>
    );
});

SideNavBar.displayName = 'SideNavBar';

export default SideNavBar;